/**
 * ReportList — displays saved reports in a table with New Report button.
 * Each report card shows name, dimensions, metrics, date, author, and Open action.
 * Uses useReports() hook to fetch saved reports for the team.
 */

import { Download, FileText, Loader2, Plus } from 'lucide-react';
import { useMemo } from 'react';

import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/context/AuthContext';
import { useReports, useExportReport } from '@/hooks/useDashboard';
import { useTeamMembers } from '@/hooks/useTeams';
import { cn } from '@/lib/utils';
import type { SavedReport } from '@/types/dashboard';
import { ReportTooLargeError } from '@/types/dashboard';

// ============================================================================
// TYPES
// ============================================================================

export interface ReportListProps {
  onNewReport: () => void;
  onOpenReport: (report: SavedReport) => void;
}

// ============================================================================
// LOADING STATE
// ============================================================================

function ReportListSkeleton() {
  return (
    <div className="rounded-[14px] bg-surface shadow-sm overflow-hidden">
      <div className="p-4 space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="h-4 w-1/4" />
            <Skeleton className="h-4 w-1/4" />
            <Skeleton className="h-4 w-1/6" />
            <Skeleton className="h-4 w-1/6" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ReportList({ onNewReport, onOpenReport }: ReportListProps) {
  const { data, isLoading } = useReports();
  // 37.1-05: services/dashboard.ts::getReports now unwraps the inner {reports: [...]}
  // envelope; the prior workaround cast was removed in this commit.
  const reports: SavedReport[] = data ?? [];

  const { currentTeam } = useAuth();
  const { data: members } = useTeamMembers(currentTeam?.teamId ?? '');
  const { exportReport, exportingId } = useExportReport();
  const { error: showError } = useToast();

  const memberNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of members ?? []) {
      const display = [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email;
      map.set(m.userId, display);
    }
    return map;
  }, [members]);

  // Phase 37 Plan 05 Task 05e (D-14 export UX): surface ReportTooLargeError
  // from the backend 413 via toast with the server's guidance message verbatim.
  // Other errors fall back to a generic retry message.
  const handleExport = async (report: SavedReport) => {
    try {
      await exportReport(report.reportId, report.name);
    } catch (err) {
      if (err instanceof ReportTooLargeError) {
        showError(err.message);
      } else {
        showError('Export failed. Please try again.');
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-[14px] bg-surface shadow-sm px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-base font-semibold text-primary">Reports</div>
          <div className="text-xs text-muted mt-0.5">
            Build custom reports by combining dimensions and metrics
          </div>
        </div>
        <button
          onClick={onNewReport}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] bg-brand text-white text-xs font-semibold hover:bg-[#1A54E8] transition-colors"
        >
          <Plus size={14} />
          New Report
        </button>
      </div>

      {/* Loading */}
      {isLoading && <ReportListSkeleton />}

      {/* Empty state */}
      {!isLoading && reports.length === 0 && (
        <div className="rounded-[14px] bg-surface shadow-sm px-4 py-12 text-center">
          <div className="w-10 h-10 rounded-full bg-surface-subtle flex items-center justify-center mx-auto mb-3">
            <FileText size={20} className="text-muted" />
          </div>
          <div className="text-sm font-medium text-secondary mb-1">
            No saved reports yet
          </div>
          <div className="text-xs text-muted mb-4">
            No saved reports yet. Create your first report to start exploring your data.
          </div>
          <button
            onClick={onNewReport}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] bg-brand text-white text-xs font-semibold hover:bg-[#1A54E8] transition-colors"
          >
            <Plus size={12} />
            New Report
          </button>
        </div>
      )}

      {/* Report table */}
      {!isLoading && reports.length > 0 && (
        <div className="rounded-[14px] bg-surface shadow-sm overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border-subtle bg-canvas">
                <th className="text-left px-4 py-2.5 text-muted font-medium">Name</th>
                <th className="text-left px-4 py-2.5 text-muted font-medium">Dimensions</th>
                <th className="text-left px-4 py-2.5 text-muted font-medium">Metrics</th>
                <th className="text-left px-4 py-2.5 text-muted font-medium">Updated</th>
                <th className="text-left px-4 py-2.5 text-muted font-medium">Author</th>
                <th className="px-4 py-2.5 text-muted font-medium">Export</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {reports.map((report, idx) => {
                const allDimensions = [
                  ...report.rowDimensions,
                  ...report.columnDimensions,
                ];
                return (
                  <tr
                    key={report.reportId}
                    className={cn(
                      'border-b border-border-subtle hover:bg-canvas cursor-pointer transition-colors',
                      idx % 2 === 1 && 'bg-canvas'
                    )}
                    onClick={() => onOpenReport(report)}
                  >
                    <td className="px-4 py-3 font-medium text-primary">
                      <div className="flex items-center gap-2">
                        <FileText size={13} className="text-brand flex-shrink-0" />
                        {report.name}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-secondary">
                      <div className="flex flex-wrap gap-1">
                        {allDimensions.map((d) => (
                          <span
                            key={d}
                            className="inline-block px-1.5 py-0.5 rounded bg-surface-subtle text-secondary text-[10px]"
                          >
                            {d}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-secondary">
                      <div className="flex flex-wrap gap-1">
                        {report.metrics.map((m) => (
                          <span
                            key={m}
                            className="inline-block px-1.5 py-0.5 rounded bg-success/10 text-success text-[10px]"
                          >
                            {m}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted whitespace-nowrap">
                      {formatDate(report.updatedAt)}
                    </td>
                    <td className="px-4 py-3 text-muted">
                      {memberNameMap.get(report.createdBy) ?? report.createdBy}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExport(report);
                        }}
                        disabled={exportingId === report.reportId}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[8px] border border-border-subtle text-xs font-medium text-brand hover:bg-canvas transition-colors disabled:opacity-50"
                      >
                        {exportingId === report.reportId ? (
                          <>
                            <Loader2 size={12} className="animate-spin" />
                            Exporting...
                          </>
                        ) : (
                          <>
                            <Download size={12} />
                            Download full CSV
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenReport(report);
                        }}
                        className="text-xs text-brand hover:underline"
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
